import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "motion/react";
import {
  useRive,
  useStateMachineInput,
  EventType,
  Layout,
  Fit,
  Alignment,
} from "@rive-app/react-webgl2";
import type { Rive } from "@rive-app/react-webgl2";
import WebglRive from "@/components/ui/WebglRive";
import vovyPoster from "@/assets/vovy/vovy.webp";


/**
 * VovyGuide — the flying tour guide for the Vovy Go landing page.
 *
 * One Vovy on screen at any time. In the hero it drifts after the cursor
 * with damped spring physics; on scroll it flies stop-to-stop, landing
 * beside each feature section and showing one speech bubble at a time.
 *
 * Stops are located by DOM anchors: place an element with
 * `data-vovy-stop="<id>"` inside each section where Vovy should land.
 * The hero cursor zone is the element with `data-vovy-hero`.
 *
 * Reduced motion: no flying — Vovy is pinned statically inside each
 * anchor (rendered via portals) with its bubble alongside, as a poster.
 *
 * Mobile (< 768px): also no flying — a fixed-position mascot chasing
 * scroll on a phone fights the thumb and covers copy. Vovy is instead
 * pinned inside each anchor as a live (idle / rocket) Rive, mounted only
 * while its pad is near the viewport so a page with five pads doesn't hold
 * five WebGL contexts at once.
 */

export interface GuideStop {
  /** Matches the `data-vovy-stop` attribute on the section's anchor. */
  id: string;
  /** Speech bubble copy shown when Vovy lands at this stop. */
  bubble?: string;
  /** Play the celebratory animation while parked at this stop. */
  celebrate?: boolean;
  /** Keep Vovy at his large hero size while parked here. */
  large?: boolean;
  /** Mirror Vovy horizontally so he faces this stop's demo panel. */
  flip?: boolean;
  /** Show the rocket-riding Vovy instead of the walking body while parked here. */
  rocket?: boolean;
}

interface VovyGuideProps {
  stops: GuideStop[];
}

interface StaticPin {
  el: HTMLElement;
  key: string;
  bubble?: string;
  large?: boolean;
  flip?: boolean;
  rocket?: boolean;
}

// talking Vovy (WebGL2 runtime): the "Mouth Vovy" talking track layers on
// top of the body clips so Vovy is visibly speaking — idle body normally,
// gesturing body while a speech bubble is on screen
export const VOVY_SRC = "/animations/talking.riv";
const VOVY_ARTBOARD = "V CODI RRG 2";
const ANIM_IDLE = ["V Speak Idle", "Mouth Vovy"];
const ANIM_SPEAK = ["V Speak Left", "Mouth Vovy"];
// celebration reuses the site's established "great" overlay wiring
const GREAT_SRC = "/animations/great.riv";
const GREAT_SM = "Vovy on Modal 2";

/** hero mode is -1; otherwise the index into `stops`. */
const HERO = -1;

/** Vovy is 2x larger in the hero (and `large` stops) than while flying. */
const sizeFor = (large: boolean, compact: boolean) =>
  large
    ? compact
      ? { w: 154, h: 179 }
      : { w: 256, h: 294 }
    : compact
      ? { w: 77, h: 90 }
      : { w: 128, h: 147 };

/** Inner Rive mascot — same load/poster/fallback pattern as VovyRive. */
function GuideVovy({ speaking }: { speaking: boolean }) {
  const [isReady, setIsReady] = useState(false);
  const [hasFailed, setHasFailed] = useState(() => {
    if (typeof document === "undefined") return false;
    try {
      return !document.createElement("canvas").getContext("webgl2");
    } catch {
      return true;
    }
  });

  const { rive, RiveComponent } = useRive(
    !hasFailed
      ? {
          src: VOVY_SRC,
          autoplay: true,
          layout: new Layout({ fit: Fit.Contain, alignment: Alignment.Center }),
        }
      : null,
    { shouldUseIntersectionObserver: false },
  );

  const speakingRef = useRef(speaking);
  speakingRef.current = speaking;

  useEffect(() => {
    if (isReady || hasFailed) return;
    const t = window.setTimeout(() => setHasFailed(true), 5000);
    return () => window.clearTimeout(t);
  }, [isReady, hasFailed]);

  const didInitRef = useRef(false);

  // Play the current clips without ever blanking the canvas. The one-time
  // reset() (the way StickyRiveWidget drives this same file) clears the
  // artboard's state machine, whose entry state hides Vovy; after that,
  // clip swaps only touch the running animations so the mascot never
  // disappears mid-flight.
  const playCurrent = (r: Rive & { startRendering?: () => void }) => {
    const names = speakingRef.current ? [...ANIM_SPEAK] : [...ANIM_IDLE];
    if (!didInitRef.current) {
      try {
        r.reset({ artboard: VOVY_ARTBOARD, animations: names, autoplay: false });
        didInitRef.current = true;
      } catch {
        /* keep whatever artboard is loaded */
      }
    }
    try {
      r.play(names);
    } catch {
      try {
        r.play();
      } catch {
        /* poster stays */
      }
    }
    r.startRendering?.();
  };

  // swap idle ↔ speak body when the bubble appears/disappears — stop only
  // the outgoing body clip (the mouth keeps talking) so there's no blank frame
  useEffect(() => {
    if (!rive || !isReady) return;
    const r = rive as Rive & { startRendering?: () => void };
    try {
      r.stop(speaking ? ANIM_IDLE[0] : ANIM_SPEAK[0]);
    } catch {
      /* nothing was playing */
    }
    playCurrent(r);
  }, [rive, isReady, speaking]);

  useEffect(() => {
    if (!rive) return;
    const r = rive as Rive & { startRendering?: () => void };
    const markReady = () => {
      playCurrent(r);
      requestAnimationFrame(() => setIsReady(true));
    };
    markReady();
    r.on(EventType.Load, markReady);
    // one-shot clips replay so Vovy is always animated
    const onStop = () => playCurrent(r);
    r.on(EventType.Stop, onStop);
    const onLoadError = () => setHasFailed(true);
    r.on(EventType.LoadError, onLoadError);
    // safety net: if playback ever stalls, kick it (same as WebglRive)
    const watchdog = window.setInterval(() => {
      try {
        if (!r.isPlaying) playCurrent(r);
      } catch {
        /* ignore */
      }
    }, 1200);
    return () => {
      r.off(EventType.Load, markReady);
      r.off(EventType.Stop, onStop);
      r.off(EventType.LoadError, onLoadError);
      window.clearInterval(watchdog);
    };
     
  }, [rive]);

  const showPoster = !isReady || hasFailed;

  return (
    <div className="relative w-full h-full">
      <img
        src={vovyPoster}
        alt=""
        aria-hidden="true"
        decoding="async"
        draggable={false}
        className={`absolute inset-0 w-full h-full object-contain pointer-events-none select-none transition-opacity duration-300 ${
          showPoster ? "opacity-100" : "opacity-0"
        }`}
      />
      {!hasFailed && <RiveComponent className="relative w-full h-full" />}
    </div>
  );
}

/**
 * Celebratory Vovy — great.riv driven exactly like VovyGreatOverlay:
 * modalState=2 (Great) + showModal advances Appear → Great in → Great loop.
 */
function CelebrateVovy() {
  const { rive, RiveComponent } = useRive({
    src: GREAT_SRC,
    autoplay: true,
    stateMachines: GREAT_SM,
    layout: new Layout({ fit: Fit.Contain, alignment: Alignment.Center }),
  });
  const modalState = useStateMachineInput(rive, GREAT_SM, "modalState");
  const showModal = useStateMachineInput(rive, GREAT_SM, "showModal");

  useEffect(() => {
    if (!rive || !modalState || !showModal) return;
    try {
      modalState.value = 2;
      showModal.fire();
      rive.play(GREAT_SM);
    } catch {
      /* stays hidden until inputs resolve */
    }
  }, [rive, modalState, showModal]);

  return <RiveComponent className="relative w-full h-full" />;
}

/** Rocket-riding Vovy — used at stops that want the launch pose instead of
 * the walking body (e.g. pricing). Same rocket asset as the dashboard's
 * "Continue learning" hero. */
function RocketVovy() {
  return <WebglRive src="/animations/rocket.riv" lazy className="w-full h-full" />;
}

/**
 * A parked mascot for the static (mobile) mode: the same Rive body the flying
 * guide uses, but mounted lazily — only once its pad is within `rootMargin`
 * of the viewport — and unmounted again when it scrolls far away, so at most
 * one or two WebGL canvases are alive on a phone. Poster until then.
 */
function StaticPinVovy({ rocket, flip }: { rocket?: boolean; flip?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setNear(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => setNear(entry.isIntersecting),
      { rootMargin: "300px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className="w-full h-full"
      style={{ transform: flip ? "scaleX(-1)" : undefined }}
    >
      {near ? (
        rocket ? (
          <RocketVovy />
        ) : (
          <GuideVovy speaking={false} />
        )
      ) : (
        <img
          src={vovyPoster}
          alt="Vovy"
          decoding="async"
          draggable={false}
          className="w-full h-full object-contain select-none"
        />
      )}
    </div>
  );
}

/**
 * Speech bubble styled with the site's card tokens. Sits centered above
 * Vovy's head — the landing pad reserves that column, so the bubble never
 * covers surrounding section text.
 */
function GuideBubble({ text, bottom }: { text: string; bottom: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.85, y: 6, x: "-50%" }}
      animate={{ opacity: 1, scale: 1, y: 0, x: "-50%" }}
      exit={{ opacity: 0, scale: 0.9, y: 4, x: "-50%" }}
      transition={{ type: "spring", stiffness: 260, damping: 20 }}
      style={{ bottom }}
      className="pointer-events-auto absolute left-1/2 w-max max-w-[220px] rounded-2xl border bg-card text-card-foreground shadow-lg px-4 py-3 text-sm leading-snug"
      role="status"
    >
      {text}
      <span
        aria-hidden="true"
        className="absolute -bottom-[5px] left-1/2 -translate-x-1/2 w-2.5 h-2.5 rotate-45 border border-t-0 border-l-0 bg-card"
      />
    </motion.div>
  );
}

const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

// Vovy (and its speech bubble) always keep this margin from every screen edge.
const EDGE = 20;
// The bubble is wider/taller than Vovy's box: centered above his head it
// overhangs each side and extends upward. Account for it when clamping.
const BUBBLE_WIDTH = 220;
const BUBBLE_RISE = 100;

const VovyGuide = ({ stops }: VovyGuideProps) => {
  const prefersReducedMotion = useReducedMotion();
  const [mode, setMode] = useState<number>(HERO);
  // seeded from the real viewport so the flying Vovy is never painted for a
  // frame in the mobile hero before the first measurement lands
  const [isCompact, setIsCompact] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 768,
  );

  const [staticAnchors, setStaticAnchors] = useState<StaticPin[]>([]);

  const modeRef = useRef<number>(HERO);
  const cursorRef = useRef<{ x: number; y: number } | null>(null);
  const hasFinePointer = useRef(
    typeof window !== "undefined" && window.matchMedia("(pointer: fine)").matches,
  );

  // Vovy's position: x in viewport coords, y in DOCUMENT coords. The y
  // spring targets a document position, and the rendered value subtracts
  // live scrollY — so while parked, Vovy rides the page 1:1 with the
  // user's scroll, and the spring only smooths real flights between
  // stops. x and y use slightly different springs so long flights arc
  // gently instead of moving in a straight line.
  const { scrollY } = useScroll();
  const x = useMotionValue(typeof window !== "undefined" ? window.innerWidth * 0.5 - 110 : 0);
  const y = useMotionValue(typeof window !== "undefined" ? window.innerHeight * 0.55 : 0);
  const sx = useSpring(x, { stiffness: 70, damping: 20, mass: 1 });
  const sy = useSpring(y, { stiffness: 60, damping: 19, mass: 1 });
  // hero cursor-tracking rides on a second, much snappier spring layered on
  // top of the flight spring, so Vovy hugs the cursor closely without
  // making the stop-to-stop flights twitchy
  const ox = useMotionValue(0);
  const oy = useMotionValue(0);
  const sox = useSpring(ox, { stiffness: 130, damping: 18, mass: 0.7 });
  const soy = useSpring(oy, { stiffness: 130, damping: 18, mass: 0.7 });
  const screenX = useTransform([sx, sox], ([a, b]) => (a as number) + (b as number));
  const screenY = useTransform(
    [sy, scrollY, soy],
    ([a, b, c]) => (a as number) - (b as number) + (c as number),
  );

  const size = sizeFor(mode >= 0 && !!stops[mode]?.large, isCompact);

  // Static mode: reduced motion, or a phone — either way Vovy is pinned in
  // the anchors instead of flying. Tracked with its own resize listener so
  // the flight effect below can tear down / rebuild on the breakpoint.
  const isStatic = prefersReducedMotion || isCompact;
  useEffect(() => {
    const onResize = () => setIsCompact(window.innerWidth < 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ── flight logic: scroll / resize drive the spring target ──────────────
  useEffect(() => {
    if (isStatic) return;

    const update = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const compact = vw < 768;
      const hero = document.querySelector<HTMLElement>("[data-vovy-hero]");
      const anchors = stops.map((s) =>
        document.querySelector<HTMLElement>(`[data-vovy-stop="${s.id}"]`),
      );

      // pick the last stop whose anchor sits in the active band of the viewport
      let next = HERO;
      anchors.forEach((el, i) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.top < vh * 0.62 && rect.bottom > 40) next = i;
      });
      if (next === HERO && hero) {
        const heroRect = hero.getBoundingClientRect();
        // keep the previous stop while between sections below the hero
        if (heroRect.bottom < vh * 0.3) next = modeRef.current === HERO ? 0 : modeRef.current;
      }

      if (next !== modeRef.current) {
        modeRef.current = next;
        setMode(next);
      }

      const { w, h } = sizeFor(next >= 0 && !!stops[next]?.large, compact);
      let tx: number;
      let ty: number;
      if (next === HERO) {
        // hero: fully magnetic — Vovy chases the cursor anywhere in the
        // hero viewport via the snappy offset spring; without a cursor he
        // floats below the copy
        const contentRect = hero
          ?.querySelector<HTMLElement>("[data-vovy-hero-content]")
          ?.getBoundingClientRect();
        // the hero reserves a pad under the CTA on md+; centre on it when
        // present so Vovy never has to be clamped up over the copy
        const padRect = hero
          ?.querySelector<HTMLElement>("[data-vovy-hero-pad]")
          ?.getBoundingClientRect();
        const heroRect = hero?.getBoundingClientRect();
        const minTy = compact ? 120 : 88;
        const maxTy = Math.max(
          minTy,
          Math.min(heroRect ? heroRect.bottom - h - EDGE : vh - h - EDGE, vh - h - EDGE),
        );
        tx = clamp(vw / 2 - w / 2, EDGE, vw - w - EDGE);
        const restY =
          padRect && padRect.height > 0
            ? padRect.top + padRect.height / 2 - h / 2
            : contentRect
              ? contentRect.bottom + 24
              : vh * 0.6;
        ty = clamp(restY, minTy, maxTy);
        const cursor = hasFinePointer.current ? cursorRef.current : null;
        if (cursor) {
          const cx = clamp(cursor.x - w / 2, EDGE, vw - w - EDGE);
          const cy = clamp(cursor.y - h / 2, minTy, maxTy);
          ox.set(cx - tx);
          oy.set(cy - ty);
        } else {
          ox.set(0);
          oy.set(0);
        }
      } else {
        ox.set(0);
        oy.set(0);
        const rect = anchors[next]?.getBoundingClientRect();
        tx = rect ? rect.left + rect.width / 2 - w / 2 : vw / 2 - w / 2;
        ty = rect ? rect.top + rect.height / 2 - h / 2 : vh * 0.55;
      }

      // keep Vovy AND his bubble fully on screen with the edge margin
      const hasBubble = next >= 0 && !!stops[next]?.bubble;
      const overhangX = hasBubble ? Math.max(0, (BUBBLE_WIDTH - w) / 2) : 0;
      const minX = EDGE + overhangX;
      const maxX = vw - w - EDGE - overhangX;
      tx = clamp(tx, minX, Math.max(minX, maxX));
      // Only the hero clamps Vovy to the viewport. At a stop he sits exactly
      // on its reserved landing pad and scrolls away with it, so he is never
      // pinned to the top or bottom edge over the content around the pad.
      if (next === HERO) {
        const navSafeTop = compact ? 120 : EDGE;
        const minTy = hasBubble ? Math.max(navSafeTop, EDGE + BUBBLE_RISE) : navSafeTop;
        ty = clamp(ty, minTy, Math.max(minTy, vh - h - EDGE));
      }
      // tx/ty are the VISUAL box position; convert to the fixed-size outer
      // box (the visual is a centered scale transform inside it)
      const boxSize = sizeFor(true, compact);
      x.set(tx - (boxSize.w - w) / 2);
      // store y as a document coordinate so Vovy scrolls with the page
      y.set(ty - (boxSize.h - h) / 2 + window.scrollY);
    };

    const onMouseMove = (e: MouseEvent) => {
      cursorRef.current = { x: e.clientX, y: e.clientY };
      if (modeRef.current === HERO) update();
    };
    const onResize = () => {
      setIsCompact(window.innerWidth < 768);
      update();
    };

    setIsCompact(window.innerWidth < 768);
    update();
    if (hasFinePointer.current) {
      window.addEventListener("mousemove", onMouseMove, { passive: true });
    }
    // re-measure once the hero entrance animation and fonts settle
    const settle1 = window.setTimeout(update, 450);
    const settle2 = window.setTimeout(update, 1300);
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", onResize);
    // Lazy sections and images below the fold change the page height after
    // Vovy has already parked; without this he keeps the pad's OLD document
    // position until the next scroll and drifts onto the heading.
    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => update())
        : null;
    ro?.observe(document.body);
    // ...and a slow poll for shifts the observer can't see (an anchor moving
    // without the body changing size). One rect read every 500ms is free;
    // only calls update() when the parked anchor actually moved.
    let lastTop: number | null = null;
    const poll = window.setInterval(() => {
      const m = modeRef.current;
      if (m < 0) return;
      const el = document.querySelector<HTMLElement>(`[data-vovy-stop="${stops[m]?.id}"]`);
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      if (lastTop !== null && Math.abs(top - lastTop) > 1) update();
      lastTop = top;
    }, 500);
    return () => {
      window.clearTimeout(settle1);
      window.clearTimeout(settle2);
      window.clearInterval(poll);
      ro?.disconnect();
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mousemove", onMouseMove);
    };
  }, [stops, isStatic, x, y, ox, oy]);

  // ── static mode: pin Vovy inside each anchor ───────────────────────────
  useEffect(() => {
    if (!isStatic) {
      setStaticAnchors([]);
      return;
    }
    // The sections owning the anchors are lazy (Suspense) — they are not in
    // the DOM at mount, so re-scan whenever the tree changes until every
    // stop has a pad, then stop watching.
    let lastKey = "";
    const scan = () => {
      const pins: StaticPin[] = [];
      stops.forEach((s) => {
        const el = document.querySelector<HTMLElement>(`[data-vovy-stop="${s.id}"]`);
        if (el) pins.push({ el, key: s.id, bubble: s.bubble, large: s.large, flip: s.flip, rocket: s.rocket });
      });
      const key = pins.map((p) => p.key).join("|");
      if (key !== lastKey) {
        lastKey = key;
        setStaticAnchors(pins);
      }
      return pins.length === stops.length;
    };
    if (scan()) return;
    let raf = 0;
    const mo = new MutationObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (scan()) mo.disconnect();
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(raf);
      mo.disconnect();
    };
  }, [stops, isStatic]);

  if (isStatic) {
    return (
      <>
        {staticAnchors.map((pin) =>
          createPortal(
            <div className="flex flex-col items-center gap-2 w-full h-full">
              {prefersReducedMotion ? (
                <img
                  src={vovyPoster}
                  alt="Vovy"
                  decoding="async"
                  draggable={false}
                  className={`object-contain select-none ${pin.large ? "w-40 h-44" : "w-14 h-16"} ${
                    pin.flip ? "-scale-x-100" : ""
                  }`}
                />
              ) : (
                // fills the pad the section reserved for the flying Vovy —
                // same footprint, so nothing else moves between the modes
                <div className={pin.large ? "w-full h-full" : "w-20 h-[5.75rem]"}>
                  <StaticPinVovy rocket={pin.rocket} flip={pin.flip} />
                </div>
              )}
              {pin.bubble && (
                <div className="rounded-2xl border bg-card text-card-foreground shadow-lg px-4 py-3 text-sm leading-snug max-w-[220px]">
                  {pin.bubble}
                </div>
              )}
            </div>,
            pin.el,
            pin.key,
          ),
        )}
      </>
    );
  }

  const activeBubble = mode >= 0 ? stops[mode]?.bubble : undefined;
  const celebrating = mode >= 0 && !!stops[mode]?.celebrate;
  const flipped = mode >= 0 && !!stops[mode]?.flip;
  const rocketing = mode >= 0 && !!stops[mode]?.rocket;

  // The box (and the Rive canvas inside it) stays at the fixed hero size —
  // resizing the canvas mid-animation desyncs Rive's drawing buffer and
  // crops the artwork. Size changes render as a pure GPU scale transform.
  const box = sizeFor(true, isCompact);
  const scale = size.w / box.w;

  // On mobile the hero renders an inline Vovy above the header text, so the
  // flying guide should stay out of the hero and only appear once he flies to
  // his first stop.
  const hideForMobileHero = isCompact && mode === HERO;

  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-40">
      <motion.div
        style={{
          x: screenX,
          y: screenY,
          width: box.w,
          height: box.h,
          willChange: "transform",
          opacity: hideForMobileHero ? 0 : 1,
          // visibility (not just opacity) so no stray frame of Vovy can paint
          // over the mobile hero while the springs settle
          visibility: hideForMobileHero ? "hidden" : "visible",
        }}
        className={`absolute top-0 left-0 ${hideForMobileHero ? "" : "transition-opacity duration-300"}`}

      >
        {/* gentle idle bob + smooth size scaling, layered on the flight spring.
            Rocket stops skip the bob — "locked" in place above the copy
            instead of drifting. */}
        <motion.div
          animate={{ y: rocketing ? 0 : [0, -7, 0], scale }}
          transition={{
            y: rocketing
              ? { type: "spring", stiffness: 120, damping: 18 }
              : { duration: 4.2, repeat: Infinity, ease: "easeInOut" },
            scale: { type: "spring", stiffness: 55, damping: 16 },
          }}
          className="w-full h-full"
        >
          {/* Horizontal mirror so Vovy can face a stop's demo panel. Applied
              flat, not animated: springing scaleX from 1 to -1 has to pass
              through 0, so Vovy squashed to a vertical sliver and sprang back
              out mirrored every time you scrolled between the two rows. The
              facing is worth keeping; watching him turn inside out is not. */}
          <div
            style={{ transform: flipped ? "scaleX(-1)" : undefined }}
            className="w-full h-full"
          >
            {celebrating ? <CelebrateVovy /> : rocketing ? <RocketVovy /> : <GuideVovy speaking={!!activeBubble} />}
          </div>
        </motion.div>
        <AnimatePresence mode="wait">
          {activeBubble && (
            <GuideBubble
              key={mode}
              text={activeBubble}
              bottom={(box.h + size.h) / 2 + 12}
            />
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default VovyGuide;
